import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  /** Shown instead of the children after an error; `reset` renders them again. */
  fallback: (props: { error: unknown; reset: () => void }) => ReactNode;
  /** Called on `reset`, e.g. to clear failed queries before rendering again. */
  onReset?: () => void;
  /** Reports the error, e.g. to monitoring. Without personal data in the message. */
  onError?: (error: unknown, info: ErrorInfo) => void;
  children: ReactNode;
}

interface State {
  failed: boolean;
  error: unknown;
}

/**
 * Keeps an error inside one part of the page, e.g. a tile on the start page, so the rest of the
 * page keeps working. React offers this only as a class component.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { failed: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly reset = () => {
    this.props.onReset?.();
    this.setState({ failed: false, error: null });
  };

  override render(): ReactNode {
    if (this.state.failed) {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }
    return this.props.children;
  }
}
